import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import {
  GridApi,
  RowNode,
  SelectionChangedEvent,
  GetRowIdParams,
  IServerSideGetRowsParams,
  GridOptions
} from 'ag-grid-community';
import isequal from 'lodash.isequal';

import GridV2 from 'app/components/AdvancedGrid/GridV2';
import { formatFilterForRequest } from 'app/components/AdvancedGrid/Sheets/AccountRule/AccountRuleHelpers';

import { CELL_HEIGHT } from 'app/constants/DataTrayConstants';

import { useBattleCard } from 'app/contexts/battleCardProvider';
import { useDataTray } from 'app/contexts/dataTrayProvider';
import { defaultSortModel, useGrid } from 'app/contexts/gridProvider';
import { usePlanTargets } from 'app/contexts/planTargetsProvider';
import { useTerritoryDefineAndRefine } from 'app/contexts/territoryDefineAndRefineProvider';

import { useUser } from 'app/core/userManagement/userProvider';

import { SplitFeatures } from 'app/global/features';
import { BLOCK_SIZE, HIERARCHY_PREVIEW_COUNT } from 'app/global/variables';

import {
  GetTerritoryRules,
  GetTerritoryRulesSortModel,
  GetTerritoryRulesVariables
} from 'app/graphql/generated/graphqlApolloTypes';
import { useGetTerritoryRulesLazy } from 'app/graphql/queries/getTerritoryRules';

import useCanUser from 'app/hooks/useCanUser';
import useShowToast from 'app/hooks/useShowToast';
import useTreatment from 'app/hooks/useTreatment';

import { FilterInput, GridFields, TerritoryGridRow } from 'app/models';

import block from 'utils/bem-css-modules';
import { getLeafNodes } from 'utils/helpers/index';
import { formatMessage } from 'utils/messages/utils';
import { UserAction } from 'utils/permissions/userActions';

import style from './TerritoryGrid.module.pcss';
import buildTerritoryColumnDef from './TerritoryGridColumnDef';
import TerritoryGridHeader from './TerritoryGridHeader';
import { formatRowData } from './territoryGridUtils';

const b = block(style);

interface TerritoryGridProps {
  onEditTerritory: (rowData) => void;
  setSelectedNodes: (nodes: RowNode[]) => void;
  setMoveTerritoriesTGTypes: Dispatch<SetStateAction<Array<number>>>;
  onIsFilterPresentChange: (isFilterActive: boolean) => void;
  onSelectionReset: () => void;
}

const TerritoryGrid: React.FC<TerritoryGridProps> = ({
  onEditTerritory,
  setSelectedNodes,
  setMoveTerritoriesTGTypes,
  onIsFilterPresentChange,
  onSelectionReset
}) => {
  const {
    refreshGrid,
    setRefreshGrid,
    setShowAggregatedActivities,
    setSelectedTerritory,
    setSelectedCell,
    isBulkDeleteChecked,
    setIsBulkDeleteChecked,
    bulkDeleteTerritoryJobKey,
    bulkDeleteExclusionIds,
    setBulkDeleteExclusionIds,
    bulkDeleteInclusionIds,
    setBulkDeleteInclusionIds
  } = useGrid();
  const { setTerritoryGroupId } = useDataTray();
  const { userRole } = useUser();
  const { selectedQuotaComponentId, selectedBattleCardId } = useBattleCard();
  const { selectedPillIdPlanTargets } = usePlanTargets();
  const { selectedPillIdTDR, tdrTreeLookupMap } = useTerritoryDefineAndRefine();
  const [isBulkDeleteTerOn] = useTreatment(SplitFeatures.BULK_DELETION_TERRITORIES);
  const [isAddedConfigurationsForUserPermissionsEnabled] = useTreatment(
    SplitFeatures.ADDED_CONFIGURATIONS_FOR_USER_PERMISSIONS
  );
  const userCanEditTerritoryDefinition = useCanUser(UserAction.TERRITORY_DEFINITION_AND_ASSIGNMENT_EDIT);
  const userCanEditTerritory = isAddedConfigurationsForUserPermissionsEnabled ? userCanEditTerritoryDefinition : true;
  const [territoryGroupTypeLookup, setTerritoryGroupTypeLookup] = useState<Record<string, string>>({});
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [currentFetchRowIndex, setCurrentFetchRowIndex] = useState(0);
  const [savedFilterModel, setSavedFilterModel] = useState<FilterInput>({});

  const showToast = useShowToast();

  const draftFilterModel = useRef<FilterInput>({});

  const selectedPillId = selectedPillIdPlanTargets || selectedPillIdTDR;
  const isBattleCardSelected = !selectedPillId || selectedPillIdPlanTargets === 'battlecard';

  const [getTerritoryRules, { fetchMore }] = useGetTerritoryRulesLazy({
    fetchPolicy: 'network-only',
    onError() {
      showToast(formatMessage('TERRITORY_GRID_ERROR'), 'danger');
    }
  });

  const activeFiltersCount = useMemo(
    () => Object.values(savedFilterModel).filter(({ filter }) => !!filter || filter === 0).length,
    [savedFilterModel]
  );

  useEffect(() => {
    onIsFilterPresentChange(activeFiltersCount > 0);
  }, [activeFiltersCount]);

  const handleFilterChange = (updatedFilter) => {
    const { filterType, type, filter, colId } = updatedFilter;
    draftFilterModel.current = {
      ...draftFilterModel.current,
      [colId]: {
        filterType,
        type,
        filter,
        filterTo: null
      }
    };
  };
  const handleClearAll = () => {
    draftFilterModel.current = {};
    handleFilterApply();
  };

  const handleFilterApply = () => {
    if (!isequal(draftFilterModel.current, savedFilterModel) && gridApi) {
      setSavedFilterModel(draftFilterModel.current);
      setSelectedNodes(null);
    }
  };

  const handleFilterClear = (colId: string) => {
    const { [colId]: _omitted, ...newFilters } = draftFilterModel.current;
    draftFilterModel.current = newFilters;
    handleFilterApply();
  };

  const handleCellClicked = (gridEvent) => {
    setSelectedCell({
      rowIndex: gridEvent.rowIndex,
      value: gridEvent.value,
      column: gridEvent.colDef.field,
      ruleId: String(gridEvent.data?.ruleId)
    });
    setTerritoryGroupId(gridEvent.data?.territoryGroupId?.toString());
  };

  const handleSelectionChanged = (gridEvent: SelectionChangedEvent) => {
    const selectionState = gridEvent.api.getServerSideSelectionState();

    // if select all checkbox was clicked, toggle bulk delete state
    if (gridEvent?.source === 'uiSelectAll') {
      setIsBulkDeleteChecked((prev) => !prev);
    }

    const selectedNodeIds = selectionState.toggledNodes;
    const formattedNodes = selectedNodeIds
      ? selectedNodeIds.map((nodeId) => {
          return { data: gridEvent.api.getRowNode(nodeId)?.data };
        })
      : [];
    const selectedTGTypes = [];
    formattedNodes.forEach((node) => {
      const tgId = node.data?.territoryGroupId;
      const tgType = territoryGroupTypeLookup[tgId];

      if (selectedTGTypes.indexOf(tgType) === -1) {
        selectedTGTypes.push(tgType);
      }
    });

    const notSelectedNodeId = [];
    gridEvent.api.forEachNode((node) => {
      if (node.data && !node.isSelected()) {
        notSelectedNodeId.push(node.data.ruleId);
      }
    });

    setMoveTerritoriesTGTypes(selectedTGTypes);
    setSelectedNodes(formattedNodes as RowNode[]);
    setBulkDeleteExclusionIds(notSelectedNodeId);
  };

  useEffect(() => {
    setMoveTerritoriesTGTypes([]);
  }, [selectedBattleCardId]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const isBulkDeleteInProgress = !!bulkDeleteTerritoryJobKey;
    gridApi.setGridOption('loading', isBulkDeleteInProgress);

    if (isBulkDeleteInProgress) {
      // reset grid selection state when bulk delete is started
      gridApi.deselectAll();
    }
  }, [bulkDeleteTerritoryJobKey, gridApi]);

  useEffect(() => {
    if (refreshGrid) {
      setRefreshGrid(false);
    }

    gridApi?.setGridOption('serverSideDatasource', {
      getRows: async (params: IServerSideGetRowsParams) => {
        // ag-grid uses 0-based indexing, but the backend uses 1-based indexing
        const startRow = params?.request?.startRow + 1;
        const endRow = params?.request?.endRow;
        const hasUserNotScrolled = endRow === BLOCK_SIZE;
        const hasSortModelBeenSet = !!params?.request?.sortModel?.[0];
        const canUseInitialData = hasUserNotScrolled;
        const newSortModel = hasSortModelBeenSet
          ? { ...(params?.request?.sortModel?.[0] as GetTerritoryRulesSortModel) }
          : defaultSortModel;

        const getTerritoryRulesVariables = {
          quotaComponentId: selectedQuotaComponentId,
          territoryGroupId: +selectedPillId || null,
          battlecardId: isBattleCardSelected ? +selectedBattleCardId : null,
          measureId: 0,
          searchInput: {
            filters: formatFilterForRequest(savedFilterModel)
          },
          sorting: newSortModel,
          dimensionsAndModifiersLimit: HIERARCHY_PREVIEW_COUNT
        };

        try {
          if (canUseInitialData) {
            params?.api?.hideOverlay();
            const territoryRulesData = await getTerritoryRules({
              variables: { ...getTerritoryRulesVariables, startRow: 1, endRow: BLOCK_SIZE }
            });
            const territoryRules = territoryRulesData?.data?.getTerritoryRules?.territoryRules || [];

            // data from the initial query is sufficient when the user has not scrolled
            const rowData = formatRowData(territoryRulesData?.data?.getTerritoryRules, startRow);

            params?.success({ rowData, rowCount: territoryRulesData?.data?.getTerritoryRules?.totalCount });
            if (!territoryRules.length) {
              params?.api?.showNoRowsOverlay();
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } else {
            params?.api?.hideOverlay();
            const fetchMoreTerritoryRules = await fetchMore<GetTerritoryRules, GetTerritoryRulesVariables>({
              variables: {
                ...getTerritoryRulesVariables,
                startRow,
                endRow,
                sorting: newSortModel
              }
            });
            const rowData = formatRowData(fetchMoreTerritoryRules?.data?.getTerritoryRules, startRow);

            params?.success({ rowData, rowCount: fetchMoreTerritoryRules?.data?.getTerritoryRules?.totalCount });
            if (!fetchMoreTerritoryRules?.data?.getTerritoryRules?.territoryRules?.length) {
              params?.api?.showNoRowsOverlay();
            }
          }

          setCurrentFetchRowIndex(params?.request?.startRow);
        } catch (error) {
          // ignore abort error until @apollo/client is upgraded
          // https://github.com/apollographql/apollo-client/issues/10533
          if ((error as Error).name !== 'AbortError') {
            throw error;
          }
        }
      }
    });
  }, [gridApi, refreshGrid, selectedQuotaComponentId, selectedPillId, selectedBattleCardId, savedFilterModel]);

  const handleGridReady = (gridEvent) => {
    setGridApi(gridEvent.api);
  };

  // listen to the checked state that is controlled by header checkbox cell renderer
  // on check/uncheck, select/deselect all rows that inside the grid
  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const allRowNodes = [];
    gridApi.forEachNode((node) => {
      if (node.data) allRowNodes.push(node);
    });
    const rowNodeCount = allRowNodes.length;

    if (isBulkDeleteChecked) {
      // only call node.setSelected(true) for the last row node inside the grid to trigger the grid onSelectionChanged event
      // call node.setSelected(true, false, true) to only check the row and suppress onSelectionChanged event
      allRowNodes.forEach((node, index) => {
        if (index + 1 === rowNodeCount) {
          node.setSelected(true);
        } else {
          node.setSelected(true, false, true);
        }
      });
      setBulkDeleteInclusionIds(allRowNodes.map((node) => node.data.ruleId));
    } else {
      allRowNodes.forEach((node, index) => {
        if (index + 1 === rowNodeCount) {
          node.setSelected(false);
        } else {
          node.setSelected(false, false, true);
        }
      });
      setBulkDeleteInclusionIds([]);
    }
  }, [isBulkDeleteChecked, gridApi]);

  // whenever there is new data load into the grid, and user have checked select all, check all new rows that rendered
  useEffect(() => {
    if (!gridApi) {
      return;
    }
    // ag grid SSRM has a configuration maxConcurrentDatasourceRequests (default value is 2)
    // hence only if the grid data is more than 3 times the cacheBlockSize, and the user scroll down the grid, it will load new data
    // 3 because when the grid is ready, the first block is already rendered, hence 2+1
    if (currentFetchRowIndex >= 3 * BLOCK_SIZE && isBulkDeleteChecked) {
      const allRowNodes = [];
      gridApi.forEachNode((node) => {
        if (node.data) allRowNodes.push(node);
      });
      const nodesToBeChecked = allRowNodes.filter(
        (node) =>
          !bulkDeleteInclusionIds.includes(node.data.ruleId) && !bulkDeleteExclusionIds.includes(node.data.ruleId)
      );
      const toBeCheckedLength = nodesToBeChecked.length;
      // only call node.setSelected(true) for the last row node inside the grid to trigger the grid onSelectionChanged event
      // call node.setSelected(true, false, true) to only check the row and suppress onSelectionChanged event
      nodesToBeChecked.forEach((node, index) => {
        if (index + 1 === toBeCheckedLength) {
          node.setSelected(true);
        } else {
          node.setSelected(true, false, true);
        }
      });
      setBulkDeleteInclusionIds((prev) => [...prev, ...nodesToBeChecked.map((node) => node.data.ruleId)]);
    }
  }, [currentFetchRowIndex, gridApi]);

  const gridProps: GridOptions = {
    headerHeight: CELL_HEIGHT,
    rowHeight: CELL_HEIGHT,
    rowModelType: 'serverSide',
    rowSelection: {
      mode: isBulkDeleteTerOn ? 'multiRow' : 'singleRow',
      enableClickSelection: isBulkDeleteTerOn ? 'enableSelection' : userCanEditTerritory,
      checkboxes: isBulkDeleteTerOn && userCanEditTerritory
    },
    selectionColumnDef: {
      pinned: 'left'
    },
    suppressCellFocus: true,
    onGridReady: handleGridReady,
    onCellClicked: handleCellClicked,
    onSelectionChanged: handleSelectionChanged,
    suppressServerSideFullWidthLoadingRow: true,
    loadingCellRenderer: 'defaultLoadingCellRenderer',
    cacheBlockSize: BLOCK_SIZE,
    columnDefs: buildTerritoryColumnDef({
      activityDrillIn: (rowData: TerritoryGridRow | Array<Record<string, unknown>>): void => {
        if (rowData) {
          setSelectedTerritory({
            territoryId: rowData[GridFields.TERRITORY_ID],
            ruleId: rowData[GridFields.RULE_ID],
            territoryGroupId: rowData[GridFields.TERRITORY_GROUP_ID]
          });
        }
        setShowAggregatedActivities(true);
      },
      onEditTerritory,
      userRole
    }),
    overlayNoRowsTemplate: formatMessage('NO_GRID_TERRITORIES_ADDED'),
    suppressMultiSort: true,
    getRowId: (params: GetRowIdParams) => `R::${params?.data?.ruleId}`
  };

  useEffect(() => {
    onSelectionReset();
    setCurrentFetchRowIndex(0);
  }, [selectedBattleCardId, selectedPillId, selectedQuotaComponentId]);

  useEffect(() => {
    if (tdrTreeLookupMap) {
      const tgTypeLookup = {};
      const tdrTree = tdrTreeLookupMap[selectedBattleCardId] || [];
      const leafNodes = [];
      tdrTree.forEach((treeNode) => {
        tgTypeLookup[treeNode.territoryGroupId] = treeNode.hierarchyTopId;
        getLeafNodes(treeNode, leafNodes);
      });
      leafNodes.forEach((node) => {
        tgTypeLookup[node.territoryGroupId] = node.hierarchyTopId;
      });

      setTerritoryGroupTypeLookup(tgTypeLookup);
    }
  }, [tdrTreeLookupMap, selectedBattleCardId]);

  return (
    <div className={b('gridWrapper')}>
      <TerritoryGridHeader
        onFilterChange={handleFilterChange}
        onFilterApply={handleFilterApply}
        data-testid="territory-grid-header"
        activeFiltersCount={activeFiltersCount}
        territoryFilters={draftFilterModel.current}
        onClearAll={handleClearAll}
        onClearField={handleFilterClear}
      />
      <div className={b('advancedGridWrapper')}>
        <GridV2 data-testid="territory-grid" {...gridProps} />
      </div>
    </div>
  );
};

export default TerritoryGrid;
